// frontend/src/widgets/MetricGraphWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-03
// Updated:      2026-09-08 16:14 BST
//
// Description:
//   Administrative historical system-metrics graph for inspecting CPU, memory,
//   disk, GPU, network, and uptime telemetry.
//
// Lifecycle Status:
//   Active administrative diagnostic widget.
//
// Change Summary (2026-08-27):
//   - Removed automatic metric loading on widget mount.
//   - Removed 30-second background polling.
//   - System metrics are now loaded only by deliberate user action.
//   - Separated component selection from metric selection.
//   - Added access to all historical metrics currently exposed by the backend.
//   - Added CPU temperature and 1-minute load average.
//   - Added GPU temperature.
//   - Added memory and disk capacity metrics.
//   - Added network sent/received metrics.
//   - Added system uptime.
//   - Added explicit loading, error, empty, and last-refreshed states.
//   - Added metric-aware units, axis domains, tooltips, and threshold bands.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves one render path for desktop and mobile.
//   - Stacks Component, Metric, Range and Load/Refresh controls on narrow screens.
//   - Makes all mobile controls full-width and touch-friendly.
//   - Converts Latest/Average/Minimum/Maximum summary cards to a 2x2 mobile grid
//     while retaining four columns at desktop widths.
//   - Gives the Recharts ResponsiveContainer a stable mobile chart height so the
//     graph does not collapse inside natural-height mobile widget layouts.
//   - Allows selection metadata, errors and empty/loading states to wrap safely.
//   - Uses natural height on mobile while retaining desktop flex-fill behaviour.
//
// API / Authentication Alignment (2026-09-08):
//   - Replaced direct axios usage with the canonical axiosInstance.
//   - The historical metrics endpoint remains explicitly user-triggered only.
//   - No automatic request, polling interval or mount-time fetch is introduced.
//
// Operational Notes:
//   - GET /api/system-metrics/history must never be called automatically.
//   - Mounting this widget must generate zero system-metrics requests.
//   - Changing component, metric, or time range does not trigger a request.
//   - The user must explicitly select Load Graph or Refresh.
//   - GPU memory and GPU power metrics are intentionally not shown because
//     they are not currently persisted by the backend.
//
// Future Intent:
//   - Add GPU memory utilisation and power telemetry when the backend collector
//     and system_metrics schema support those measurements.
//   - Consider configurable warning/critical thresholds sourced from backend
//     configuration rather than frontend constants.

import React, {
  useCallback,
  useMemo,
  useState,
} from "react";

import dayjs from "dayjs";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import axiosInstance from "../api/axiosInstance";

const API_PATH =
  "/system-metrics/history";

const COMPONENTS = {
  cpu: {
    label: "CPU",
    metrics: [
      {
        key: "cpu_percent",
        label: "Load",
        unit: "%",
        precision: 1,
        domain: [0, 100],
        thresholds: {
          warning: 70,
          critical: 90,
        },
      },
      {
        key: "cpu_temp_c",
        label: "Temperature",
        unit: "°C",
        precision: 1,
        domain: [0, 110],
        thresholds: {
          warning: 75,
          critical: 90,
        },
      },
      {
        key: "load_avg_1m",
        label: "1 Minute Load Average",
        unit: "",
        precision: 2,
        domain: [
          "auto",
          "auto",
        ],
      },
    ],
  },

  memory: {
    label: "Memory",
    metrics: [
      {
        key: "mem_used_percent",
        label: "Used",
        unit: "%",
        precision: 1,
        domain: [0, 100],
        thresholds: {
          warning: 70,
          critical: 90,
        },
      },
      {
        key: "mem_used_gb",
        label: "Used",
        unit: "GB",
        precision: 2,
        domain: [
          0,
          "auto",
        ],
      },
      {
        key: "mem_total_gb",
        label: "Total",
        unit: "GB",
        precision: 2,
        domain: [
          0,
          "auto",
        ],
      },
    ],
  },

  disk: {
    label: "Disk",
    metrics: [
      {
        key: "disk_used_percent",
        label: "Used",
        unit: "%",
        precision: 1,
        domain: [0, 100],
        thresholds: {
          warning: 80,
          critical: 95,
        },
      },
      {
        key: "disk_used_gb",
        label: "Used",
        unit: "GB",
        precision: 2,
        domain: [
          0,
          "auto",
        ],
      },
      {
        key: "disk_total_gb",
        label: "Total",
        unit: "GB",
        precision: 2,
        domain: [
          0,
          "auto",
        ],
      },
    ],
  },

  gpu: {
    label: "GPU",
    metrics: [
      {
        key: "gpu_load_percent",
        label: "Load",
        unit: "%",
        precision: 1,
        domain: [0, 100],
        thresholds: {
          warning: 70,
          critical: 90,
        },
      },
      {
        key: "gpu_temp_c",
        label: "Temperature",
        unit: "°C",
        precision: 1,
        domain: [0, 110],
        thresholds: {
          warning: 75,
          critical: 90,
        },
      },
    ],
  },

  network: {
    label: "Network",
    metrics: [
      {
        key: "net_sent_mb",
        label: "Data Sent",
        unit: "MB",
        precision: 2,
        domain: [
          0,
          "auto",
        ],
      },
      {
        key: "net_recv_mb",
        label: "Data Received",
        unit: "MB",
        precision: 2,
        domain: [
          0,
          "auto",
        ],
      },
    ],
  },

  uptime: {
    label: "System",
    metrics: [
      {
        key: "uptime_sec",
        label: "Uptime",
        unit: "hours",
        precision: 1,
        domain: [
          0,
          "auto",
        ],
        transform: (value) =>
          Number.isFinite(
            Number(value)
          )
            ? Number(value) /
              3600
            : null,
      },
    ],
  },
};

const TIME_RANGES = [
  {
    value: "1h",
    label: "Last 1 hour",
    amount: 1,
    unit: "hour",
  },
  {
    value: "6h",
    label: "Last 6 hours",
    amount: 6,
    unit: "hour",
  },
  {
    value: "24h",
    label: "Last 24 hours",
    amount: 24,
    unit: "hour",
  },
  {
    value: "7d",
    label: "Last 7 days",
    amount: 7,
    unit: "day",
  },
];

const MetricGraphWidget = () => {
  const [
    component,
    setComponent,
  ] = useState("cpu");

  const [
    metricKey,
    setMetricKey,
  ] = useState(
    "cpu_percent"
  );

  const [
    timeRange,
    setTimeRange,
  ] = useState("1h");

  const [
    data,
    setData,
  ] = useState([]);

  const [
    loadedSelection,
    setLoadedSelection,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    lastRefreshedAt,
    setLastRefreshedAt,
  ] = useState(null);

  const componentDefinition =
    COMPONENTS[component];

  const selectedMetric =
    useMemo(
      () =>
        componentDefinition.metrics.find(
          (item) =>
            item.key ===
            metricKey
        ) ||
        componentDefinition
          .metrics[0],
      [
        componentDefinition,
        metricKey,
      ]
    );

  const handleComponentChange =
    (event) => {
      const nextComponent =
        event.target.value;

      const nextDefinition =
        COMPONENTS[
          nextComponent
        ];

      setComponent(
        nextComponent
      );

      setMetricKey(
        nextDefinition
          .metrics[0].key
      );
    };

  const fetchMetrics =
    useCallback(
      async () => {
        const definition =
          COMPONENTS[
            component
          ];

        const metricDefinition =
          definition.metrics.find(
            (item) =>
              item.key ===
              metricKey
          ) ||
          definition.metrics[0];

        const range =
          TIME_RANGES.find(
            (item) =>
              item.value ===
              timeRange
          ) ||
          TIME_RANGES[0];

        const now =
          dayjs();

        const from =
          now.subtract(
            range.amount,
            range.unit
          );

        setLoading(true);
        setError("");

        try {
          const response =
            await axiosInstance.get(
              API_PATH,
              {
                params: {
                  from:
                    from.toISOString(),
                  to:
                    now.toISOString(),
                  metric:
                    component,
                },
              }
            );

          const rows =
            Array.isArray(
              response
                ?.data
                ?.data
            )
              ? response
                  .data
                  .data
              : [];

          const transformed =
            rows
              .map(
                (entry) => {
                  const rawValue =
                    entry?.[
                      metricDefinition
                        .key
                    ];

                  if (
                    rawValue ===
                      null ||
                    rawValue ===
                      undefined
                  ) {
                    return null;
                  }

                  const numericValue =
                    Number(
                      rawValue
                    );

                  if (
                    !Number.isFinite(
                      numericValue
                    )
                  ) {
                    return null;
                  }

                  const value =
                    metricDefinition.transform
                      ? metricDefinition.transform(
                          numericValue
                        )
                      : numericValue;

                  if (
                    value ===
                      null ||
                    !Number.isFinite(
                      Number(
                        value
                      )
                    )
                  ) {
                    return null;
                  }

                  return {
                    timestamp:
                      entry.timestamp,
                    timestampLabel:
                      dayjs(
                        entry.timestamp
                      ).format(
                        timeRange ===
                          "7d"
                          ? "DD MMM HH:mm"
                          : "HH:mm"
                      ),
                    value:
                      Number(
                        value
                      ),
                  };
                }
              )
              .filter(
                Boolean
              );

          setData(
            transformed
          );

          setLoadedSelection(
            {
              component,
              componentLabel:
                definition.label,
              metricKey:
                metricDefinition.key,
              metricLabel:
                metricDefinition.label,
              unit:
                metricDefinition.unit,
              precision:
                metricDefinition.precision,
              domain:
                metricDefinition.domain,
              thresholds:
                metricDefinition.thresholds ||
                null,
              timeRange,
              timeRangeLabel:
                range.label,
            }
          );

          setLastRefreshedAt(
            new Date()
          );
        } catch (
          requestError
        ) {
          console.error(
            "[MetricGraphWidget] Failed to load metric history:",
            requestError
          );

          setData([]);

          setError(
            requestError
              ?.response
              ?.data
              ?.detail ||
              requestError
                ?.message ||
              "Failed to load system metric history."
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        component,
        metricKey,
        timeRange,
      ]
    );

  const formatValue = (
    value,
    selection =
      loadedSelection
  ) => {
    if (
      value === null ||
      value === undefined ||
      !selection
    ) {
      return "—";
    }

    const numeric =
      Number(value);

    if (
      !Number.isFinite(
        numeric
      )
    ) {
      return "—";
    }

    const formatted =
      numeric.toFixed(
        selection.precision ??
          1
      );

    return selection.unit
      ? `${formatted} ${selection.unit}`
      : formatted;
  };

  const latestValue =
    data.length > 0
      ? data[
          data.length - 1
        ]?.value ?? null
      : null;

  const minimumValue =
    data.length > 0
      ? Math.min(
          ...data.map(
            (item) =>
              item.value
          )
        )
      : null;

  const maximumValue =
    data.length > 0
      ? Math.max(
          ...data.map(
            (item) =>
              item.value
          )
        )
      : null;

  const averageValue =
    data.length > 0
      ? data.reduce(
          (
            sum,
            item
          ) =>
            sum +
            item.value,
          0
        ) /
        data.length
      : null;

  const graphDomain =
    loadedSelection?.domain ||
    [
      "auto",
      "auto",
    ];

  const graphThresholds =
    loadedSelection
      ?.thresholds ||
    null;

  const summaryItems = [
    {
      label: "Latest",
      value:
        latestValue,
    },
    {
      label: "Average",
      value:
        averageValue,
    },
    {
      label: "Minimum",
      value:
        minimumValue,
    },
    {
      label: "Maximum",
      value:
        maximumValue,
    },
  ];

  return (
    <div className="flex h-auto min-h-0 w-full flex-col overflow-visible p-3 md:h-full md:overflow-hidden">
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:flex md:flex-wrap md:items-end">
        <div className="min-w-0">
          <label
            htmlFor="metric-graph-component"
            className="mb-1 block text-xs font-medium"
          >
            Component
          </label>

          <select
            id="metric-graph-component"
            className="min-h-[44px] w-full rounded border px-3 py-2 text-sm md:min-h-0 md:w-auto md:px-2 md:py-1"
            value={
              component
            }
            onChange={
              handleComponentChange
            }
            disabled={
              loading
            }
          >
            {Object.entries(
              COMPONENTS
            ).map(
              ([
                key,
                definition,
              ]) => (
                <option
                  key={
                    key
                  }
                  value={
                    key
                  }
                >
                  {
                    definition.label
                  }
                </option>
              )
            )}
          </select>
        </div>

        <div className="min-w-0">
          <label
            htmlFor="metric-graph-metric"
            className="mb-1 block text-xs font-medium"
          >
            Metric
          </label>

          <select
            id="metric-graph-metric"
            className="min-h-[44px] w-full rounded border px-3 py-2 text-sm md:min-h-0 md:w-auto md:max-w-[260px] md:px-2 md:py-1"
            value={
              metricKey
            }
            onChange={(
              event
            ) =>
              setMetricKey(
                event.target
                  .value
              )
            }
            disabled={
              loading
            }
          >
            {componentDefinition.metrics.map(
              (metric) => (
                <option
                  key={
                    metric.key
                  }
                  value={
                    metric.key
                  }
                >
                  {
                    metric.label
                  }
                  {metric.unit
                    ? ` (${metric.unit})`
                    : ""}
                </option>
              )
            )}
          </select>
        </div>

        <div className="min-w-0">
          <label
            htmlFor="metric-graph-range"
            className="mb-1 block text-xs font-medium"
          >
            Range
          </label>

          <select
            id="metric-graph-range"
            className="min-h-[44px] w-full rounded border px-3 py-2 text-sm md:min-h-0 md:w-auto md:px-2 md:py-1"
            value={
              timeRange
            }
            onChange={(
              event
            ) =>
              setTimeRange(
                event.target
                  .value
              )
            }
            disabled={
              loading
            }
          >
            {TIME_RANGES.map(
              (range) => (
                <option
                  key={
                    range.value
                  }
                  value={
                    range.value
                  }
                >
                  {
                    range.label
                  }
                </option>
              )
            )}
          </select>
        </div>

        <div className="flex items-end sm:col-span-2 md:block">
          <button
            type="button"
            onClick={
              fetchMetrics
            }
            disabled={
              loading
            }
            className="min-h-[44px] w-full rounded border px-3 py-2 text-sm font-medium disabled:opacity-50 md:min-h-0 md:w-auto md:py-1"
          >
            {loading
              ? "Loading..."
              : loadedSelection
                ? "Refresh"
                : "Load Graph"}
          </button>
        </div>
      </div>

      {loadedSelection && (
        <div className="mb-3 flex min-w-0 flex-col gap-1 text-xs sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-2">
          <div
            className="min-w-0 break-words"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            <span className="font-medium">
              {
                loadedSelection.componentLabel
              }{" "}
              —{" "}
              {
                loadedSelection.metricLabel
              }
            </span>

            <span className="ml-2">
              {
                loadedSelection.timeRangeLabel
              }
            </span>
          </div>

          {lastRefreshedAt && (
            <div className="shrink-0 text-gray-500">
              Updated{" "}
              {dayjs(
                lastRefreshedAt
              ).format(
                "HH:mm:ss"
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mb-3 break-words rounded border p-3 text-sm"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          {error}
        </div>
      )}

      {!loadedSelection &&
        !loading && (
          <div className="flex min-h-[180px] flex-1 items-center justify-center px-3 text-center text-sm leading-6 text-gray-500">
            Select a component, metric and time range, then choose Load Graph.
          </div>
        )}

      {loadedSelection &&
        !loading &&
        !error &&
        data.length === 0 && (
          <div className="flex min-h-[180px] flex-1 items-center justify-center px-3 text-center text-sm leading-6 text-gray-500">
            No historical data is available for the selected metric and time
            range.
          </div>
        )}

      {loading && (
        <div
          className="flex min-h-[180px] flex-1 items-center justify-center px-3 text-center text-sm text-gray-500"
          aria-live="polite"
        >
          Loading metric history...
        </div>
      )}

      {!loading &&
        !error &&
        data.length > 0 && (
          <>
            <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
              {summaryItems.map(
                (item) => (
                  <div
                    key={
                      item.label
                    }
                    className="min-w-0 rounded border p-2"
                  >
                    <div className="text-xs text-gray-500">
                      {
                        item.label
                      }
                    </div>

                    <div
                      className="break-words text-sm font-semibold"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {formatValue(
                        item.value
                      )}
                    </div>
                  </div>
                )
              )}
            </div>

            <div className="h-[280px] min-h-[280px] w-full min-w-0 md:h-auto md:min-h-[250px] md:flex-1">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <LineChart
                  data={
                    data
                  }
                  margin={{
                    top: 10,
                    right: 12,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    dataKey="timestampLabel"
                    minTickGap={
                      30
                    }
                    tick={{
                      fontSize: 10,
                    }}
                  />

                  <YAxis
                    domain={
                      graphDomain
                    }
                    tick={{
                      fontSize: 10,
                    }}
                    width={
                      48
                    }
                    tickFormatter={(
                      value
                    ) =>
                      Number.isFinite(
                        Number(
                          value
                        )
                      )
                        ? Number(
                            value
                          ).toFixed(
                            loadedSelection.precision ??
                              1
                          )
                        : value
                    }
                  />

                  <Tooltip
                    formatter={(
                      value
                    ) => [
                      formatValue(
                        value
                      ),
                      loadedSelection.metricLabel,
                    ]}
                    labelFormatter={(
                      label
                    ) =>
                      label
                    }
                  />

                  {graphThresholds && (
                    <>
                      <ReferenceArea
                        y1={
                          graphThresholds.warning
                        }
                        y2={
                          graphThresholds.critical
                        }
                        fill="#faedad"
                        strokeOpacity={
                          0
                        }
                      />

                      <ReferenceArea
                        y1={
                          graphThresholds.critical
                        }
                        y2={
                          typeof graphDomain[1] ===
                          "number"
                            ? graphDomain[1]
                            : graphThresholds.critical
                        }
                        fill="#f9dedd"
                        strokeOpacity={
                          0
                        }
                      />
                    </>
                  )}

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8884d8"
                    strokeWidth={
                      2
                    }
                    dot={
                      false
                    }
                    isAnimationActive={
                      false
                    }
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
    </div>
  );
};

export default MetricGraphWidget;
